<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div class="mt-3">
    <div class="mt-3">
      <div class="row mb-0">
        <p class="bb-sheet-title">RUNES</p>
      </div>
    </div>
    <!-- ------------------------- -->
    <!-- OVERLAY: rune side panel -->
    <!-- ------------------------- -->
    <RuneSidePanel
      v-if="isSidePanelOpen"
      :rune="character_rune"
      :character="character"
      @add-character-rune="$emit('add-character-rune', $event)"
      @update-character-rune="$emit('update-character-rune', $event)"
      @delete-character-spell="$emit('delete-character-spell', $event)"
      @delete-character-rune="$emit('delete-character-rune', $event)"
      @close-rune-panel="handleRunePanelClose"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ---------------- -->
    <!--  RUNE TABLE      -->
    <!-- ---------------- -->
    <div v-if="runes">
      <TableRunes
        ref="TableRunes"
        v-if="runes"
        :headings="headings"
        :runes="runes"
        @show-character-rune="handleShowRunePanel"
        @delete-character-rune="$emit('delete-character-rune', $event)"
      />
    </div>
  </div>
  <!-- <div v-if="!loadReady" class="bb-loader"></div>  -->
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'

import RuneSidePanel from '../components/RuneSidePanel.vue'
import TableRunes from './TableRunes.vue'

export default {
  name: 'CharacterSheetRunes',
  emits: [
    'add-character-rune',
    'update-character-rune',
    'delete-character-spell',
    'delete-character-rune',
  ],
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableRunes,
    RuneSidePanel,
  },
  //========================================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      loadReady: false,
      isSidePanelOpen: false,
      //
      character_rune: null,
      //
      runes: [],
      errors: [],
      //
      name: 'RuneList',
      headings: [
        {
          label: '#',
          field: 'rowNumber',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 5,
          label: 'Rune',
          // use derived display label (prefer display_name, fallback to name)
          field: 'display',
          width: '12em',
          filterable: false,
          sortable: false,
        },
        {
          id: 7,
          label: 'Powers',
          field: 'table_powers',
          width: '36em',
          filterable: false,
          sortable: false,
        },
        {
          id: 84,
          label: 'Linked Item',
          field: 'item_name',
          width: '14em',
          filterable: false,
          sortable: false,
        },
        {
          id: 82,
          label: 'Integrated Rune',
          field: 'integrated_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 81,
          label: 'Active Rune',
          field: 'active_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 80,
          label: 'Starter Rune',
          field: 'starter_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 7,
          label: 'Strength',
          field: 'strength',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 8,
          label: 'Power Storage',
          field: 'pwr_storage',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
        },
        {
          id: 9,
          label: 'Spell Count',
          field: 'spell_count',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
        },
        {
          id: 15,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 65,
          label: 'Delete',
          field: 'action_delete',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 108,
          label: 'Starter Flag',
          field: 'starter',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 112,
          label: 'Item',
          field: 'item_name',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 110,
          label: 'Integrated Flag',
          field: 'integrated',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 112,
          label: 'Active Flag',
          field: 'active',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 156,
          label: 'Rune Id',
          field: 'rune_id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },
      ],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    //--------------------------------------------------------------------
    // watch character so adding/removing runes updates table immediately
    //--------------------------------------------------------------------
    character: {
      handler(newChar) {
        console.log('=====================================')
        console.log('WATCH CHARACTER (SHEET RUNES)')
        if (!newChar) return
        //--------------------
        // update runes array
        //--------------------
        const sourceRunes = newChar.runes ?? []
        const allSpells = Array.isArray(newChar.spells) ? newChar.spells : []

        // normalise into table rows:
        // - keep originalIndex for stable row numbers
        // - add display field (display_name || name) for UI
        // - recompute spell_count from current rune-linked spells so
        //   add/delete operations update the count immediately
        this.runes = sourceRunes
          .map((r, idx) => {
            const sourceIndex =
              r.originalIndex !== undefined ? r.originalIndex : idx
            const display =
              r.display_name && r.display_name !== ''
                ? r.display_name
                : r.name || ''
            const trait = (r.trait_name || r.trait || '').toString().trim()
            const shortPowers = (r.short_powers || r.powers || '')
              .toString()
              .trim()
            const table_powers =
              trait !== ''
                ? shortPowers !== ''
                  ? `${trait}. ${shortPowers}`
                  : trait
                : shortPowers

            const runeId = r.id != null ? String(r.id) : null
            let spell_count = 0

            if (runeId) {
              spell_count = allSpells.filter((s) => {
                if (!s) return false

                // Only consider rune-sourced spells
                const sourceLabel = (s.source || '').toString().toLowerCase()
                if (sourceLabel !== 'rune') return false

                // Support both backend and local spell representations
                const spellRuneId =
                  (s.character_rune_id != null
                    ? String(s.character_rune_id)
                    : null) ||
                  (s.character_rune != null
                    ? String(s.character_rune)
                    : null) ||
                  (s.source_id != null ? String(s.source_id) : null)

                if (!spellRuneId) return false
                return spellRuneId === runeId
              }).length
            }

            return {
              ...r,
              sourceIndex,
              display,
              table_powers,
              spell_count,
            }
          })
          .sort((a, b) =>
            (a.display || '').localeCompare(b.display || '', undefined, {
              sensitivity: 'base',
              numeric: true,
            }),
          )
          .map((r, idx) => ({
            ...r,
            originalIndex: idx,
          }))
        // console.log('========== updated runes:', this.runes)
        //----------------------------------------------------------------
        // update character_rune if side panel is open (force reactivity)
        //----------------------------------------------------------------
        if (this.isSidePanelOpen && this.character_rune) {
          const updatedRune = this.runes.find(
            (r) => r.id === this.character_rune.id,
          )
          if (updatedRune) {
            console.log(
              'updating character_rune for side panel:',
              updatedRune.name,
            )
            // create new ref to trigger RuneCard watcher
            this.character_rune = { ...updatedRune }
          } else {
            console.warn('character_rune not found in updated runes')
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // computed
  //----------
  computed: {
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //========================================================
    // handle show rune panel
    //-------------------------
    handleShowRunePanel(character_rune) {
      console.log('IN handleShowRunePanel...', character_rune)
      this.character_rune = character_rune
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle rune panel close
    //--------------------------
    handleRunePanelClose() {
      // console.log('IN handleRunePanelClose...')
      // this.SetView('runes')
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
  },
}
</script>
